import PropTypes from 'prop-types';
import { createContext } from 'react';
// hooks
import useLocalStorage from 'hooks/useLocalStorage';
// theme
import palette from 'theme/palette';

// ----------------------------------------------------------------------

// Mobeat brand palettes — the theme's color picker swaps `primary` between these.
const PRIMARY_COLOR = [
  // SIGNAL — the logo's yellow; matches the app's existing look.
  {
    name: 'signal',
    lighter: '#FEF3D0',
    light: '#FAD666',
    main: '#F5B921',
    dark: '#A87C0E',
    darker: '#6B4E08',
    contrastText: palette.light.grey[800],
  },
  // PULSE — electric violet.
  {
    name: 'pulse',
    lighter: '#E7E4FB',
    light: '#A99EF2',
    main: '#6C5CE7',
    dark: '#493BB0',
    darker: '#2A1F73',
    contrastText: '#fff',
  },
  // BEAT — vivid teal.
  {
    name: 'beat',
    lighter: '#D3F3EF',
    light: '#63D5C7',
    main: '#12B3A0',
    dark: '#0B7C6F',
    darker: '#064F47',
    contrastText: '#fff',
  },
];

SetColor.propTypes = {
  themeColor: PropTypes.oneOf(['signal', 'pulse', 'beat']),
};

function SetColor(themeColor) {
  const [SIGNAL, PULSE, BEAT] = PRIMARY_COLOR;

  switch (themeColor) {
    case 'pulse':
      return PULSE;
    case 'beat':
      return BEAT;
    default:
      return SIGNAL;
  }
}

const initialState = {
  themeMode: 'dark',
  themeDirection: 'ltr',
  themeColor: 'signal',
  themeStretch: false,
  useVisx: false,
  onChangeMode: () => {},
  onChangeDirection: () => {},
  onChangeColor: () => {},
  onToggleStretch: () => {},
  onToggleVisx: () => {},
  setColor: PRIMARY_COLOR[0],
  colorOption: [],
};

const SettingsContext = createContext(initialState);

SettingsProvider.propTypes = {
  children: PropTypes.node,
};

function SettingsProvider({ children }) {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: initialState.themeMode,
    themeDirection: initialState.themeDirection,
    themeColor: initialState.themeColor,
    themeStretch: initialState.themeStretch,
    useVisx: initialState.useVisx,
  });

  const onChangeMode = (event) => {
    setSettings({
      ...settings,
      themeMode: event.target.value,
    });
  };

  const onChangeDirection = (event) => {
    setSettings({
      ...settings,
      themeDirection: event.target.value,
    });
  };

  const onChangeColor = (event) => {
    setSettings({
      ...settings,
      themeColor: event.target.value,
    });
  };

  const onToggleStretch = () => {
    setSettings({
      ...settings,
      themeStretch: !settings.themeStretch,
    });
  };

  const onToggleVisx = () => {
    setSettings({
      ...settings,
      useVisx: !settings.useVisx,
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        // Charts always use the newer Visx renderer (the toggle was removed); override any stored value.
        useVisx: true,
        // Mode
        onChangeMode,
        // Direction
        onChangeDirection,
        // Color
        onChangeColor,
        setColor: SetColor(settings.themeColor),
        colorOption: PRIMARY_COLOR.map((color) => ({
          name: color.name,
          value: color.main,
        })),
        // Stretch
        onToggleStretch,
        // Visx
        onToggleVisx,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export { SettingsProvider, SettingsContext };
